import { FC, ReactNode } from 'react';
import { getRegionColors, handleImageError } from '../../../helpers';
import { classNames } from '../../../utils';

const defaultBgColor = '#F8F8F8';

/** Get score label and color based on Wine Advocate rating system */
const getScoreInfo = (score: number): { label: string; bgColor: string } => {
  if (score >= 96) return { label: 'Extraordinary', bgColor: '#16a34a' };
  if (score >= 90) return { label: 'Outstanding', bgColor: '#22c55e' };
  if (score >= 80) return { label: 'Very Good', bgColor: '#eab308' };
  return { label: 'Below Average', bgColor: '#9ca3af' };
};

interface ProductImageProps {
  bgColor?: string;
  imageUrl?: string;
  region?: string;
  textColor?: string;
  children?: ReactNode;
  imageContainer?: string;
  imageByLwin18?: string;
  criticScore?: number;
}

const ProductImage: FC<ProductImageProps> = ({
  bgColor,
  imageUrl,
  region,
  textColor,
  children,
  imageContainer = '',
  imageByLwin18,
  criticScore,
}) => {
  const txtColor = textColor || getRegionColors(region).textColor;
  const scoreInfo = criticScore ? getScoreInfo(criticScore) : null;

  const getImageByLwin18 = () => {
    const lwin18Image = `https://cwcdst2prdctimgimprtfnct.blob.core.windows.net/height/400/${imageByLwin18?.substring(
      0,
      7,
    )}.png`;
    return lwin18Image;
  };

  return (
    <div className="relative">
      <div
        className="overflow-hidden flex flex-col justify-center items-center"
        style={{
          background: `linear-gradient(180deg, ${defaultBgColor} 0%, #EFEFEF 100%)`,
          height: '240px',
        }}
      >
        {!children && imageUrl && imageUrl.length > 0 && (
          <div className="w-full relative left-0 flex flex-col h-100 items-center">
            <div className={classNames('relative h-[240px] img-container', `${imageContainer}`.trim())}>
              <img src={imageUrl} alt="product" className="bg-transparent object-cover img drop-shadow-lg" />
            </div>
          </div>
        )}

        {!children && imageByLwin18 && imageByLwin18.length > 0 && (
          <div className="w-full relative left-0 flex flex-col h-100 items-center">
            <div className={classNames('relative max-h-[240px] img-container', `${imageContainer}`.trim())}>
              <img
                src={getImageByLwin18()}
                alt="product"
                className="bg-transparent object-cover img h-[220px] drop-shadow-lg"
                onError={handleImageError}
              />
            </div>
          </div>
        )}
        {children && (
          <div className="w-full relative left-0 flex flex-col h-100 items-start">
            <div className={`relative min-w-[375px] h-[240px] flex-1 ${imageContainer}`.trim()}>{children}</div>
          </div>
        )}
        {!children && (!imageUrl || imageUrl.length === 0) && !imageByLwin18 && (
          <div className="w-full relative left-0 flex flex-col h-100 items-start">
            <div className="relative min-w-[375px] h-[240px] flex-1" />
          </div>
        )}

        {/* Score Badge with Label */}
        {scoreInfo && criticScore && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5">
            <div
              className="flex items-center justify-center min-w-[36px] h-[36px] rounded-xl font-bold text-base text-white shadow-lg"
              style={{ backgroundColor: scoreInfo.bgColor }}
            >
              {criticScore}
            </div>
            <div
              className="px-2 py-1 rounded-lg text-xs font-medium text-white shadow-md"
              style={{ backgroundColor: scoreInfo.bgColor }}
            >
              {scoreInfo.label}
            </div>
          </div>
        )}

        {/* Region Badge */}
        {region && (
          <div
            className={classNames(
              'px-3 py-1 absolute top-3 right-3 flex rounded-xl text-xs font-semibold justify-center whitespace-nowrap items-center shadow-md',
              txtColor || '',
            )}
            style={{ background: bgColor || 'rgba(255,255,255,0.9)' }}
          >
            {region}
          </div>
        )}
      </div>

      {/* Gradient accent bar */}
      <div
        className="w-full h-[8px]"
        style={{
          background: bgColor
            ? `linear-gradient(90deg, ${bgColor} 0%, ${bgColor}CC 100%)`
            : 'linear-gradient(90deg, #1D4854 0%, #55A2A7 100%)',
        }}
      />
    </div>
  );
};

export default ProductImage;
